/**
 * Tracks nested image loads and releases Pixi's retained decode-worker pool after a
 * quiet period. A new load cancels the pending release, so no active decoder is ever
 * terminated while an atlas is still being assembled.
 */
export class AssetLoadCoordinator {
  private activeLoads = 0;
  private releaseTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly releaseWorkers: () => void,
    private readonly quietMs = 1_500,
  ) {}

  async run<T>(load: () => Promise<T>): Promise<T> {
    this.cancelRelease();
    this.activeLoads++;
    try {
      return await load();
    } finally {
      this.activeLoads--;
      if (this.activeLoads === 0) this.scheduleRelease();
    }
  }

  dispose(): void {
    this.cancelRelease();
    this.release();
  }

  private scheduleRelease(): void {
    this.cancelRelease();
    this.releaseTimer = setTimeout(() => {
      this.releaseTimer = undefined;
      if (this.activeLoads === 0) this.release();
    }, this.quietMs);
  }

  private cancelRelease(): void {
    if (this.releaseTimer === undefined) return;
    clearTimeout(this.releaseTimer);
    this.releaseTimer = undefined;
  }

  private release(): void {
    try {
      this.releaseWorkers();
    } catch {
      // Worker cleanup is an optimization. A browser-specific reset failure must not
      // turn an otherwise successful asset load into an application failure.
    }
  }
}
